import logger from '@/utils/logger';
/**
 * COMPOSANT: PRICING PLANS V2
 * Séparation claire: Abonnements mensuels VS Pay-as-you-go
 */

import React, { useState } from 'react';
import { Check, Zap, Crown, Rocket, Star, Info, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../auth/AuthProvider';
import { useTokens } from '../../hooks/useTokens';
import PaymentModal from './PaymentModal';

interface PricingPlansProps {
  currentBalance?: number;
  onPurchaseSuccess?: () => void;
}

export default function PricingPlans({ currentBalance, onPurchaseSuccess }: PricingPlansProps) {
  const { user } = useAuth();
  const { subscriptionTier } = useTokens(); // Récupérer le plan actuel
  const [selectedTab, setSelectedTab] = useState<'monthly' | 'payg'>('monthly');
  const [paygAmount, setPaygAmount] = useState(100); // Montant en euros
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<typeof monthlyPlans[0] | null>(null);

  // Plans mensuels (abonnements)
  const monthlyPlans = [
    {
      name: 'Pro',
      price: 29,
      tokens: 960000,
      features: [
        '960K tokens/mois',
        'Memory Pattern & Collisions',
        'Agents arrière-plan',
        'Intégration Notion',
        'Rollover +20%',
      ],
      popular: true,
      badge: 'P',
      gradient: 'from-green-500 to-emerald-600',
    },
    {
      name: 'Business',
      price: 79,
      tokens: 2600000,
      features: [
        '2.6M tokens/mois',
        'Big Data Analytics',
        'Pattern Detection avancée',
        'Support prioritaire',
        'Rollover +20%',
      ],
      badge: 'B',
      gradient: 'from-orange-600 to-indigo-600',
    },
    {
      name: 'Enterprise',
      price: 199,
      tokens: 6600000,
      features: [
        '6.6M tokens/mois',
        'Multi-utilisateurs',
        'API Access',
        'Support 24/7',
        'Rollover +20%',
      ],
      badge: 'E',
      gradient: 'from-purple-500 to-pink-600',
    },
  ];

  // Calcul tokens pay-as-you-go (avec bonus progressif)
  const calculatePaygTokens = (amount: number): number => {
    const baseTokens = amount * 18000; // 1€ = 18K tokens
    let bonus = 0;
    
    if (amount >= 500) bonus = 0.20;
    else if (amount >= 200) bonus = 0.15;
    else if (amount >= 100) bonus = 0.10;
    else if (amount >= 50) bonus = 0.05;
    
    return Math.round(baseTokens * (1 + bonus));
  };

  const paygTokens = calculatePaygTokens(paygAmount);
  const paygPricePerK = (paygAmount / (paygTokens / 1000)).toFixed(3);

  const handleMonthlyPlanSelect = (plan: typeof monthlyPlans[0]) => {
    if (!user) {
      alert('Vous devez être connecté pour souscrire');
      return;
    }

    logger.log('[PricingPlans] Ouverture modal pour:', plan.name);
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    logger.log('[PricingPlans] Paiement réussi !');
    if (onPurchaseSuccess) {
      onPurchaseSuccess();
    }
    // Recharger la page pour mettre à jour le profil
    window.location.reload();
  };

  const handlePaygPurchase = async () => {
    if (!user) {
      alert('Vous devez être connecté pour acheter des tokens');
      return;
    }

    setLoading(true);
    logger.log('[PricingPlans] PAYG:', paygAmount, '€');
    
    try {
      // Pour PAYG, on utilise un slug générique ou on crée dynamiquement
      // Pour l'instant, redirection simple (à implémenter selon ton système PAYG)
      alert('PAYG pas encore implémenté - utilise les plans mensuels');
      setLoading(false);
    } catch (error) {
      logger.error('[PricingPlans] Erreur:', error);
      alert(`Erreur: ${error.message}`);
      setLoading(false);
    }
  };

  return (
    <>
      {/* Payment Modal */}
      {selectedPlan && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          planName={selectedPlan.name}
          planPrice={selectedPlan.price}
          planSlug={`plan_${selectedPlan.name.toLowerCase()}`}
          planTokens={selectedPlan.tokens}
          onSuccess={handlePaymentSuccess}
        />
      )}

    <div className="w-full max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="font-display text-3xl font-medium text-theme-primary mb-3">
          Choisissez votre formule
        </h2>
        <p className="text-theme-secondary text-lg mb-6">
          Abonnement mensuel ou achat ponctuel, vous décidez
        </p>
        
        {currentBalance !== undefined && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-theme">
            <span className="page-kicker">Balance</span>
            <span className="font-mono text-sm">
              {currentBalance.toLocaleString()} tokens
            </span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex gap-1 border-b border-theme">
          <button
            onClick={() => setSelectedTab('monthly')}
            className={`studio-tab ${selectedTab === 'monthly' ? 'is-active' : ''}`}
          >
            Mensuel
          </button>
          <button
            onClick={() => setSelectedTab('payg')}
            className={`studio-tab ${selectedTab === 'payg' ? 'is-active' : ''}`}
          >
            À l'usage
          </button>
        </div>
      </div>

      {/* Contenu selon tab */}
      {selectedTab === 'monthly' ? (
        <>
          {/* Plans Mensuels */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {monthlyPlans.map((plan) => {
              return (
                <div
                  key={plan.name}
                  className={`
                    relative p-8 border bg-theme-surface
                    ${plan.popular
                      ? 'border-[var(--theme-text-primary)]'
                      : 'border-theme'
                    }
            `}
          >
            {/* Badge Popular */}
                  {plan.popular && (
                    <div className="absolute -top-3 left-6 px-2 py-0.5 bg-[var(--theme-text-primary)] text-[var(--theme-text-inverse)] text-[9px] tracking-[0.16em] uppercase">
                      Recommandé
              </div>
            )}

                  {/* Badge sobre avec initiale */}
            <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 border border-theme flex items-center justify-center">
                      <span className="font-display text-2xl">
                        {plan.badge}
                      </span>
                    </div>
            </div>

            {/* Plan Name */}
            <h3 className="text-2xl font-bold text-theme-primary text-center mb-2">
                    Clairify {plan.name}
            </h3>

            {/* Price */}
            <div className="text-center mb-6">
              <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold text-theme-primary">
                        {plan.price}€
                </span>
                      <span className="text-theme-secondary text-lg">/mois</span>
              </div>
                    <div className="text-sm text-theme-secondary mt-2">
                      {(plan.tokens / 1000).toLocaleString('fr-FR')}K tokens/mois
              </div>
            </div>

            {/* Features */}
                  <ul className="space-y-3 mb-8">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-theme-secondary">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {/* CTA Button */}
            {subscriptionTier?.toLowerCase() === plan.name.toLowerCase() ? (
              // Plan actuel - bouton désactivé
              <Button
                disabled
                className="w-full py-4 rounded-xl font-bold transition-all text-base bg-theme-surface border-2 border-green-500/50 text-green-400 cursor-not-allowed flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                PLAN ACTUEL
              </Button>
            ) : subscriptionTier !== 'free' && ['pro', 'business', 'enterprise'].indexOf(plan.name.toLowerCase()) < ['pro', 'business', 'enterprise'].indexOf(subscriptionTier?.toLowerCase()) ? (
              // Downgrade - bouton désactivé
              <Button
                disabled
                className="w-full py-4 rounded-xl font-bold transition-all text-base bg-theme-hover border border-theme-border text-theme-tertiary cursor-not-allowed"
              >
                Downgrade impossible
              </Button>
            ) : (
              // Upgrade ou souscription initiale
              <Button
                onClick={() => handleMonthlyPlanSelect(plan)}
                className={`
                  w-full py-4 rounded-xl font-bold transition-all text-base
                  ${plan.popular
                    ? 'btn-modern btn-primary'
                    : 'btn-modern btn-secondary'
                  }
                `}
              >
                {subscriptionTier === 'free' ? `Choisir ${plan.name}` : `Upgrade vers ${plan.name}`}
              </Button>
            )}
          </div>
              );
            })}
          </div>

          {/* Info Rollover */}
          <div className="p-4 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 border border-blue-500/30 dark:border-blue-400/40 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-theme-primary">
              <span className="font-semibold">Rollover +20% :</span> Les tokens non utilisés ce mois sont reportés le mois suivant (jusqu'à 20% de votre allocation mensuelle)
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Pay-as-you-go */}
          <div className="max-w-2xl mx-auto">
            <div className="p-8 rounded-2xl border-2 border-theme-border bg-theme-surface shadow-lg">
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-theme-primary mb-2">
                  Achat Ponctuel
                </h3>
                <p className="text-theme-secondary">
                  Choisissez le montant entre 20€ et 500€
                </p>
          </div>

              {/* Slider */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-theme-secondary">Montant</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-purple-600 dark:text-purple-400">
                      {paygAmount}€
                    </span>
        </div>
      </div>

                <input
                  type="range"
                  min="20"
                  max="500"
                  step="10"
                  value={paygAmount}
                  onChange={(e) => setPaygAmount(Number(e.target.value))}
                  className="w-full h-3 bg-theme-hover rounded-lg appearance-none cursor-pointer
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-6
                    [&::-webkit-slider-thumb]:h-6
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-gradient-to-r
                    [&::-webkit-slider-thumb]:from-purple-600
                    [&::-webkit-slider-thumb]:to-indigo-600
                    [&::-webkit-slider-thumb]:shadow-lg
                    [&::-webkit-slider-thumb]:cursor-pointer
                    [&::-moz-range-thumb]:w-6
                    [&::-moz-range-thumb]:h-6
                    [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:bg-gradient-to-r
                    [&::-moz-range-thumb]:from-purple-600
                    [&::-moz-range-thumb]:to-indigo-600
                    [&::-moz-range-thumb]:border-0
                    [&::-moz-range-thumb]:shadow-lg
                    [&::-moz-range-thumb]:cursor-pointer
                  "
                />
                
                <div className="flex justify-between mt-2 text-xs text-theme-tertiary">
                  <span>20€</span>
                  <span>100€</span>
                  <span>200€</span>
                  <span>500€</span>
                </div>
              </div>

              {/* Tokens calculés */}
              <div className="p-6 rounded-xl bg-gradient-to-br from-purple-500/10 dark:from-purple-500/20 to-purple-600/5 border border-purple-500/30 dark:border-purple-400/40 mb-6">
                <div className="text-center">
                  <div className="text-sm text-theme-secondary mb-2">Vous recevrez</div>
                  <div className="text-4xl font-bold text-theme-primary mb-1">
                    {paygTokens.toLocaleString('fr-FR')}
                  </div>
                  <div className="text-sm text-purple-600 dark:text-purple-400">tokens</div>
                  
                  {/* Bonus si applicable */}
                  {paygAmount >= 50 && (
                    <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 rounded-full">
                      <Zap className="w-4 h-4 text-green-400" />
                      <span className="text-xs text-green-400 font-semibold">
                        +{paygAmount >= 500 ? '20' : paygAmount >= 200 ? '15' : paygAmount >= 100 ? '10' : '5'}% bonus inclus
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-sm text-theme-secondary">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Tokens valables à vie (pas d'expiration)</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-theme-secondary">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Sans engagement (pas d'abonnement)</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-theme-secondary">
                  <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Bonus progressif jusqu'à +20%</span>
                </li>
              </ul>

              {/* CTA Button */}
              <Button
                onClick={handlePaygPurchase}
                className="w-full btn-modern btn-primary"
              >
                Acheter pour {paygAmount}€
              </Button>
            </div>

            {/* Comparaison */}
            <div className="mt-6 p-4 rounded-xl bg-yellow-500/10 dark:bg-yellow-500/20 border border-yellow-500/30 dark:border-yellow-400/40 flex items-start gap-3">
              <Info className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-700 dark:text-yellow-200">
                <span className="font-semibold">Astuce :</span> Les plans mensuels sont ~45% plus avantageux que le pay-as-you-go
              </div>
            </div>
          </div>
        </>
      )}
    </div>
    </>
  );
}
